import { ActiveSimNoPackageTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/ActiveSimNoPackageTile/ActiveSimNoPackageTile.tsx";
import { ActiveSimTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/ActiveSimTile/ActiveSimTile.tsx";
import { AddDeviceTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/AddDeviceTile/AddDeviceTile.tsx";
import { FlatSpeedTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/FlatSpeedTile/FlatSpeedTile.tsx";
import { InactiveSimTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/InactiveTile/InactiveSimTile.tsx";
import { NumberPortingTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/NumberPortingTile/NumberPortingTile.tsx";
import { PendingSimTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/PendingTile/PendingSimTile.tsx";
import { QRCodeTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/QRCodeTile/QRCodeTile.tsx";
import { SuspendedSimTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/SuspendedSimTile/SuspendedSimTile.tsx";
import { UnverifiedSimTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/UnverifiedSimTile/UnverifiedSimTile.tsx";
import { ExtendedSimCardDetail } from "@/hooks/useExtendedSims.ts";
import { SimCardDetailSimStatus } from "@/services/model";
import {
  hasEsimInfo,
  hasFlatSpeedPackage,
  hasSimId,
  isKYCPendingTile,
  isNumberPortingTile,
  isPendingEsim,
  isPendingPhysicalSim,
  needsQRCodeTile,
} from "@/utils/deviceUtils.tsx";
import { getProductCategory, getProductIdFromSim } from "@/utils/productMatrix";

export const mapDevice = (
  device: ExtendedSimCardDetail,
  needsIdCheck: boolean | undefined,
  manageRoute: string,
) => {
  // ID check first: number porting (incl. orange / missing-info) must not
  // claim "porting requested" while account KYC is still open.
  if (
    isKYCPendingTile(device, needsIdCheck) &&
    (hasSimId(device) || device.simStatus === SimCardDetailSimStatus.orange)
  ) {
    return <UnverifiedSimTile device={device} />;
  }
  if (device.simStatus === SimCardDetailSimStatus.orange) {
    return (
      <NumberPortingTile
        device={device}
        missingInfo
        manageRoute={manageRoute}
      />
    );
  }
  if (!hasSimId(device)) {
    return null;
  }
  if (isNumberPortingTile(device)) {
    return <NumberPortingTile device={device} manageRoute={manageRoute} />;
  }
  // Red (suspended) beats servicePending — same order as getSimDisplayPriority.
  if (device.simStatus === SimCardDetailSimStatus.red) {
    return <SuspendedSimTile device={device} manageRoute={manageRoute} />;
  }
  if (isPendingEsim(device)) {
    return <PendingSimTile device={device} manageRoute={manageRoute} />;
  }
  if (isPendingPhysicalSim(device)) {
    return <PendingSimTile device={device} manageRoute={manageRoute} />;
  }
  if (
    // typeguard ExtendedSimCardDetail into ExtendedSimCardDetailWithEsimInfo
    hasEsimInfo(device) &&
    needsQRCodeTile(device)
  ) {
    return <QRCodeTile device={device} />;
  }

  // "home" (router) and "data" (IoT/precharged data-only) tariffs both show
  // the download/upload speed-card footer via FlatSpeedTile -- see
  // FlatSpeedTile.tsx.
  const productCategory = getProductCategory(getProductIdFromSim(device));
  const needsFlatSpeedTile =
    productCategory === "home" || productCategory === "data";

  // No special case applies, we can display the normal tiles
  switch (device.simStatus) {
    // If the status is green, the device is active with automatic renewal of its tariff.
    // As with yellow below, a device can be green (active) purely via a
    // standalone roaming package with no flat-rate package of its own.
    case SimCardDetailSimStatus.green:
      if (!hasFlatSpeedPackage(device)) {
        return (
          <ActiveSimNoPackageTile device={device} manageRoute={manageRoute} />
        );
      }

      if (needsFlatSpeedTile) {
        return <FlatSpeedTile device={device} manageRoute={manageRoute} />;
      }

      return <ActiveSimTile device={device} manageRoute={manageRoute} />;
    // If the status is yellow, the device and its tariff will become inactive after the remaining days.
    // If there's no flat speed package, it means that we do have an active roaming package,
    // so we show a different tile, which informs of this.
    case SimCardDetailSimStatus.yellow:
      if (!hasFlatSpeedPackage(device)) {
        return (
          <ActiveSimNoPackageTile device={device} manageRoute={manageRoute} />
        );
      }

      if (needsFlatSpeedTile) {
        return <FlatSpeedTile device={device} manageRoute={manageRoute} />;
      }

      return <ActiveSimTile device={device} manageRoute={manageRoute} />;
    // If the status is grey, and no conditions above match,
    // the device has become inactive after running out of remaining days
    case SimCardDetailSimStatus.grey:
      return <InactiveSimTile device={device} manageRoute={manageRoute} />;
    default:
      return null;
  }
};

export const getMetaTiles = (addDeviceRoute: string) => (
  <>
    <AddDeviceTile addDeviceRoute={addDeviceRoute} />
  </>
);
